import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Copy, HelpCircle } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: "administrator", label: "Administrator" },
  { value: "advisor", label: "Advisor" },
  { value: "standard", label: "Standard" },
  { value: "read_only", label: "Read Only" },
  { value: "leave_admin_only", label: "Leave Admin Only" },
];

const ACCESS_RULES = [
  { value: "full", label: "Full" },
  { value: "none", label: "None" },
  { value: "read_only", label: "Read Only" },
];

// Companies are provided later; empty for now.
const COMPANIES = [];

export default function AddUser() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "administrator",
    restrict_to_companies: true,
    default_access_rule: "full",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tempResult, setTempResult] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const copyToAll = () => {
    if (COMPANIES.length === 0) {
      toast.info("No companies to copy to yet.");
      return;
    }
    toast.success("Default access copied to all companies below.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/users`, form, { withCredentials: true });
      if (data.emailed) {
        toast.success(`User created. Temporary password emailed to ${data.email}.`);
        navigate("/manage-users");
      } else {
        setTempResult(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full h-11 rounded-lg border border-slate-200 px-4 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors focus:border-[#13B5EA] focus:ring-2 focus:ring-[#13B5EA]/20";

  return (
    <div className="min-h-full bg-slate-50 px-8 py-8" data-testid="add-user-page">
      <button
        onClick={() => navigate("/manage-users")}
        data-testid="add-user-back"
        className="flex items-center gap-1.5 text-sm font-medium text-[#13B5EA] hover:underline mb-6"
      >
        <ArrowLeft size={15} /> Go back to Users
      </button>

      <h1 className="text-3xl font-bold text-[#1e2d38] mb-8">Add User</h1>

      <form onSubmit={handleSubmit} className="max-w-3xl" data-testid="add-user-form">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
          {/* Details */}
          <div className="grid grid-cols-[180px_1fr] items-center gap-x-6 gap-y-5">
            <label className="text-sm font-medium text-slate-600">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              data-testid="add-user-email"
              className={inputBase}
            />

            <label className="text-sm font-medium text-slate-600">First Name</label>
            <input
              type="text"
              required
              value={form.first_name}
              onChange={(e) => set("first_name", e.target.value)}
              data-testid="add-user-first-name"
              className={inputBase}
            />

            <label className="text-sm font-medium text-slate-600">Last Name</label>
            <input
              type="text"
              required
              value={form.last_name}
              onChange={(e) => set("last_name", e.target.value)}
              data-testid="add-user-last-name"
              className={inputBase}
            />

            <label className="text-sm font-medium text-slate-600">Permission Role</label>
            <div className="max-w-xs">
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="add-user-role" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`role-option-${r.value}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="h-px bg-slate-100" />

          {/* Restrict to companies */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <Checkbox
              checked={form.restrict_to_companies}
              onCheckedChange={(v) => set("restrict_to_companies", !!v)}
              data-testid="restrict-companies-checkbox"
            />
            <span className="text-sm font-medium text-slate-700">Restrict to selected Companies</span>
          </label>

          {form.restrict_to_companies && (
            <div className="space-y-4" data-testid="companies-section">
              {/* Default access rule */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-600">Default Access Rule</span>
                  <HelpCircle size={15} className="text-slate-400" />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={copyToAll}
                    data-testid="copy-to-all"
                    className="text-sm font-medium text-[#13B5EA] hover:underline"
                  >
                    Copy to all below
                  </button>
                  <div className="w-40">
                    <Select
                      value={form.default_access_rule}
                      onValueChange={(v) => set("default_access_rule", v)}
                    >
                      <SelectTrigger data-testid="default-access-rule" className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACCESS_RULES.map((a) => (
                          <SelectItem key={a.value} value={a.value}>
                            {a.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Companies list (empty until provided) */}
              {COMPANIES.length === 0 ? (
                <div
                  data-testid="companies-empty"
                  className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-400"
                >
                  No companies have been set up yet. Company access rows will appear here once the
                  company list is available.
                </div>
              ) : null}
            </div>
          )}

          {error && (
            <div data-testid="add-user-error" className="rounded-lg bg-red-50 border border-red-100 px-4 py-2.5 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="add-user-submit"
              className="h-11 rounded-lg bg-[#13B5EA] px-6 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#0f9fce] active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Creating..." : "Add User"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/manage-users")}
              className="h-11 rounded-lg border border-slate-200 px-6 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>

      {/* Temp password result (email not configured) */}
      <Dialog open={!!tempResult} onOpenChange={(o) => !o && (setTempResult(null), navigate("/manage-users"))}>
        <DialogContent data-testid="add-user-temp-dialog">
          <DialogHeader>
            <DialogTitle>User created</DialogTitle>
            <DialogDescription>
              {tempResult?.note ||
                "Share this temporary password with the user securely. Their account is disabled until they sign in with it."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              For <span className="font-medium text-slate-700">{tempResult?.email}</span>
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 font-mono text-sm text-slate-800 break-all">
                {tempResult?.temporary_password}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(tempResult?.temporary_password || "");
                  toast.success("Copied");
                }}
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:text-[#13B5EA] hover:border-[#13B5EA] transition-colors"
                aria-label="Copy"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => {
                setTempResult(null);
                navigate("/manage-users");
              }}
              className="h-10 rounded-lg bg-[#13B5EA] px-5 text-sm font-semibold text-white hover:bg-[#0f9fce] transition-colors"
              data-testid="add-user-done"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
